import type { PatientProfile, PatientProfileSearchResult } from '../../../contracts';
import { toPatientProfileSearchResult } from './to-patient-profile-search-result';

/**
 * Local-first patient matcher for the find-or-link combobox.
 *
 * Runs synchronously per keystroke against the already-cached patient
 * roster (`useQueryPatientProfiles`) so results appear instantly, before
 * the debounced BE Atlas Search responds. It deliberately MIRRORS the BE
 * matching semantics (see `_token_search_clause` in ml-scribe's
 * `patient_profile_repo.py` and the `patient_profile_search` Atlas index):
 *
 * - query is whitespace-tokenised, capped at 5 tokens, tokens AND-ed
 * - per token: PREFIX match on any word of the name or demographic
 *   string (names mirror the edgeGram autocomplete on `first_name` /
 *   `last_name`; demographic words are included so legacy profiles
 *   whose name only lives in the free-text string still match) OR
 *   SUBSTRING match on `demographic_string` (3+ chars) /
 *   `ehr_patient_id` / `identifiers[].value` (2+ chars), mirroring the
 *   nGram autocomplete and its minGrams on those fields
 * - lowercase + diacritic folding (mirrors `foldDiacritics`)
 *
 * One deliberate divergence: word-PREFIX matching has no length floor
 * (the Atlas edgeGram index bottoms out at 2 chars, so the BE returns
 * nothing for 1-char queries). "Name starts with B" is meaningful at
 * one keystroke — that gap is the whole reason this layer exists. The
 * SUBSTRING floors are kept, though: "contains this letter anywhere"
 * matches most of a roster and reads as noise.
 *
 * Parity matters: the merge in `useFindOrLinkSearch` never removes a
 * local row when the BE responds, so a local matcher that drifts LOOSER
 * than the BE only costs an extra plausible row. If the BE index
 * definition changes (fields, tokenisation), update this file to match.
 */

/** Mirrors `_MAX_QUERY_TOKENS` in ml-scribe's patient_profile_repo. */
const MAX_QUERY_TOKENS = 5;

/**
 * Roster items from `GET /patient-profiles/` are
 * `PatientProfileWithSessionCount` (model + session decorations). The
 * decorations are read defensively because some callers still type the
 * items as the bare `PatientProfileModel`.
 */
export type RosterPatientProfile = PatientProfile & {
  session_count?: number | null;
  last_session_created_at?: string | null;
};

export interface SearchablePatientProfile {
  /** Search-result row shape the combobox renders. */
  result: PatientProfileSearchResult;
  /**
   * Normalised LEADING word of the displayed name (first name, or the
   * demographic string's first word for legacy profiles). A prefix hit
   * here outranks mid-name hits: when the user types "S", a row
   * *displayed* as "Sofia …" must sort above "Berenice Smith" — a list
   * that leads with non-S names for query "S" reads as broken even
   * though the surname matches are correct.
   */
  leadingWord: string;
  /**
   * Normalised words of first_name + last_name PLUS the words of
   * demographic_string (prefix-matched). Demographic words are included
   * so legacy profiles with no structured name fields (name lives only
   * in the free-text demographic string) still match at 1-2 chars.
   */
  nameWords: string[];
  /** Normalised demographic_string (substring-matched). */
  demographic: string;
  /** Normalised ehr_patient_id + identifiers[].value (substring-matched). */
  identifiers: string[];
}

/**
 * Lowercase + strip diacritics — mirrors the index's `foldDiacritics`.
 * NFKD (not NFD) so compatibility forms (ﬁ-style ligatures from
 * PDF-pasted text) also decompose; strictly broader matching, which is
 * the safe direction for this layer.
 */
export function normalizeForSearch(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '');
}

/**
 * Word boundary for name words AND query tokens. Includes hyphens and
 * apostrophes (both ASCII and typographic) because the BE's Lucene
 * standard tokenizer splits "Anne-Marie" / "O'Brien" into separate
 * words before edgeGramming — splitting only on whitespace here made
 * "ma" MISS Anne-Marie locally, the one direction (local stricter than
 * BE) this matcher must never err in. Query tokens split the same way
 * so "anne-marie" typed with the hyphen still matches.
 */
const WORD_BOUNDARY = /[\s'’-]+/;

/** Tokenise on the word boundary and cap at 5 tokens — mirrors the BE. */
export function tokenizeQuery(query: string): string[] {
  return normalizeForSearch(query.trim())
    .split(WORD_BOUNDARY)
    .filter(Boolean)
    .slice(0, MAX_QUERY_TOKENS);
}

/**
 * Precompute the per-profile haystacks once per roster load. Pure —
 * memoise on the roster reference at the call site.
 */
export function buildSearchableProfiles(
  profiles: readonly RosterPatientProfile[]
): SearchablePatientProfile[] {
  return profiles.flatMap((profile) => {
    const base = toPatientProfileSearchResult(profile);
    if (!base) return [];
    return {
      result: {
        ...base,
        // Session decorations from the list payload, when present —
        // drives the dropdown's "last seen" secondary line and ranking.
        last_seen_at: profile.last_session_created_at ?? null,
        session_count: profile.session_count ?? 0,
      },
      // First non-empty word of first_name, falling back to the
      // demographic string. Deliberately NOT `??`: EHR imports send ''
      // (not null) for missing names, and '' must fall through too —
      // otherwise these profiles silently lose the leading-word rank
      // tier.
      leadingWord:
        [profile.first_name, profile.demographic_string]
          .flatMap((value) => normalizeForSearch(value ?? '').split(WORD_BOUNDARY))
          .filter(Boolean)[0] ?? '',
      nameWords: normalizeForSearch(
        [profile.first_name, profile.last_name, profile.demographic_string]
          .filter(Boolean)
          .join(' ')
      )
        .split(WORD_BOUNDARY)
        .filter(Boolean),
      demographic: normalizeForSearch(profile.demographic_string ?? ''),
      identifiers: [
        profile.ehr_patient_id,
        ...(profile.identifiers?.map((identifier) => identifier.value) ?? []),
      ]
        .filter((value): value is string => Boolean(value))
        .map(normalizeForSearch),
    };
  });
}

interface ScoredMatch {
  searchable: SearchablePatientProfile;
  /**
   * 3 = a token prefix-hit the LEADING display word ("S" → "Sofia …"),
   * 2 = a token prefix-hit some other name/demographic word ("S" →
   *     "Berenice Smith"; approximates the BE's 3x name boost),
   * 1 = substring hits only (MRN / DOB fragment).
   * Where the match is visible beats recency — recency only tie-breaks
   * within a band.
   */
  score: number;
}

/**
 * Per-field minimum token lengths for the SUBSTRING fields, mirroring
 * the Atlas index's nGram minGrams (`demographic_string` 3,
 * `ehr_patient_id` / `identifiers.value` 2). Without these, a 1-char
 * token matches every roster row containing that letter anywhere in its
 * demographic text — pure noise. Name-word PREFIX matching deliberately
 * has no floor (the BE's edgeGram minGrams 2 is an index limitation,
 * not a product choice — going to 1 char there is this layer's whole
 * point, and "name starts with B" is a meaningful result).
 */
const MIN_DEMOGRAPHIC_TOKEN_LENGTH = 3;
const MIN_IDENTIFIER_TOKEN_LENGTH = 2;

function matchProfile(
  searchable: SearchablePatientProfile,
  tokens: string[]
): ScoredMatch | null {
  let anyNameHit = false;
  let anyLeadingHit = false;
  for (const token of tokens) {
    const nameHit = searchable.nameWords.some((word) => word.startsWith(token));
    const otherHit =
      (token.length >= MIN_DEMOGRAPHIC_TOKEN_LENGTH &&
        searchable.demographic.includes(token)) ||
      (token.length >= MIN_IDENTIFIER_TOKEN_LENGTH &&
        searchable.identifiers.some((identifier) => identifier.includes(token)));
    if (!nameHit && !otherHit) return null; // tokens are AND-ed
    if (nameHit) anyNameHit = true;
    if (searchable.leadingWord.startsWith(token)) anyLeadingHit = true;
  }
  const score = anyLeadingHit ? 3 : anyNameHit ? 2 : 1;
  return { searchable, score };
}

/**
 * Match + rank the roster against a live (undebounced) query. Ranking:
 * name-prefix hits first, then most recently seen, then most sessions,
 * then alphabetical — a stable, deterministic order so the list never
 * reshuffles between keystrokes for the same surviving rows.
 */
export function searchLocalProfiles(
  searchables: readonly SearchablePatientProfile[],
  query: string,
  limit: number
): PatientProfileSearchResult[] {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const matches: ScoredMatch[] = [];
  for (const searchable of searchables) {
    const match = matchProfile(searchable, tokens);
    if (match) matches.push(match);
  }

  matches.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    const aSeen = a.searchable.result.last_seen_at;
    const bSeen = b.searchable.result.last_seen_at;
    if (aSeen !== bSeen) {
      if (!aSeen) return 1; // nulls last
      if (!bSeen) return -1;
      // ISO timestamps — most recent first. Plain comparison, not
      // localeCompare: digits/'-'/':' collate consistently and this
      // keeps the order identical on every user's machine.
      return aSeen < bSeen ? 1 : aSeen > bSeen ? -1 : 0;
    }
    if (a.searchable.result.session_count !== b.searchable.result.session_count) {
      return b.searchable.result.session_count - a.searchable.result.session_count;
    }
    // Fixed locale so the last-resort alphabetical tie-break doesn't
    // vary with the user's OS locale.
    return a.searchable.result.demographic_string.localeCompare(
      b.searchable.result.demographic_string,
      'en'
    );
  });

  return matches.slice(0, limit).map((match) => match.searchable.result);
}
